import React, { useEffect, useRef } from "react";
import { Outlet, Link } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { useCurrency, CURRENCIES } from "@/context/CurrencyContext";
import { useStore } from "@/context/StoreContext";
import { useSound } from "@/context/SoundContext";
import { CraftsCartProvider, useCraftsCart } from "@/context/CraftsCartContext";
import CraftsCart from "@/components/crafts/CraftsCart";

const CraftsBanner = () => {
  const { settings } = useStore();
  if (!settings?.crafts_banner_enabled || !settings?.crafts_banner_text) return null;
  return (
    <div data-testid="crafts-banner" className="bg-gold text-cocoa text-center px-4 py-2.5 text-[0.66rem] sm:text-xs tracking-[0.16em] uppercase font-medium">
      {settings.crafts_banner_text}
    </div>
  );
};

const CraftsHeader = () => {
  const { currency, setCurrency } = useCurrency();
  const { count, setOpen } = useCraftsCart();
  return (
    <header data-testid="crafts-header" className="sticky top-0 z-40 bg-cocoa/95 backdrop-blur-md border-b border-gold/15">
      <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
        <Link to="/crafts" data-testid="crafts-wordmark" className="leading-none">
          <span className="font-serif text-cream text-xl sm:text-2xl tracking-[0.14em]">NOIRBYAMARAORI</span>
          <span className="block text-[0.58rem] tracking-[0.42em] uppercase text-gold mt-1">Crafts</span>
        </Link>
        <div className="flex items-center gap-4">
          <select
            data-testid="crafts-currency"
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            className="bg-transparent border border-cream/20 text-cream/80 text-[0.68rem] tracking-[0.12em] uppercase px-3 py-1.5 outline-none hover:border-gold transition-colors cursor-pointer">
            {Object.keys(CURRENCIES).map((c) => <option key={c} value={c} className="bg-cocoa text-cream">{c}</option>)}
          </select>
          <button onClick={() => setOpen(true)} data-testid="crafts-bag-btn" className="relative text-cream hover:text-gold transition-colors p-1">
            <ShoppingBag size={20} />
            {count > 0 && (
              <span data-testid="crafts-bag-count" className="absolute -top-1.5 -right-1.5 bg-gold text-cocoa text-[0.6rem] w-4 h-4 rounded-full flex items-center justify-center font-medium">{count}</span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
};

const CraftsLayout = () => {
  // Silence the storefront "welcome to ORÍ" playlist while inside Crafts (product videos keep their own audio); resume on leaving.
  const sound = useSound();
  const soundRef = useRef(sound);
  soundRef.current = sound;
  useEffect(() => {
    window.__nbaSuppressSound = true;
    soundRef.current?.duck?.();
    return () => {
      window.__nbaSuppressSound = false;
      soundRef.current?.unduck?.();
    };
  }, []);
  return (
  <CraftsCartProvider>
    <div className="min-h-screen bg-cocoa text-cream flex flex-col" style={{ backgroundColor: "#2E1A13" }}>
      <CraftsBanner />
      <CraftsHeader />
      <main className="flex-1"><Outlet /></main>
      <footer className="border-t border-gold/15 mt-24">
        <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="font-serif text-lg tracking-[0.12em] text-cream">NOIRBYAMARAORI <span className="text-gold">· Crafts</span></p>
          <p className="text-[0.62rem] tracking-[0.22em] uppercase text-cream/40">Digital goods · Glasgow · United Kingdom</p>
        </div>
      </footer>
      <CraftsCart />
    </div>
  </CraftsCartProvider>
  );
};

export default CraftsLayout;
